/**
 * Экран принятия приглашения: человек назначает себе пароль.
 *
 * Открывается по ссылке из письма, без входа — у человека ещё нет пароля,
 * он его здесь и выбирает. Ничего, кроме имени и двух полей: это не
 * приложение, а дверь в него.
 */

import { useEffect, useState, type FormEvent } from 'react'

import { ApiError, api, type InviteInfo } from './api'
import { BackgroundMap } from './BackgroundMap'
import { LangPicker } from './LangPicker'
import { TruckIcon } from './icons'
import { dict, type Lang } from './i18n'

/** Те же 10 знаков, что требует сервер: подсказать раньше, чем откажут. */
const MIN_PASSWORD = 10

type Props = {
  token: string
  lang: Lang
  onLang: (lang: Lang) => void
  center: { lat: number; lon: number }
}

export function Invite({ token, lang, onLang, center }: Props) {
  const t = dict(lang)
  const [info, setInfo] = useState<InviteInfo | null>(null)
  const [gone, setGone] = useState(false)
  const [password, setPassword] = useState('')
  const [repeat, setRepeat] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)

  useEffect(() => {
    let cancelled = false
    api
      .inviteInfo(token)
      .then((i) => {
        if (!cancelled) setInfo(i)
      })
      .catch((caught) => {
        // 404 — ссылка отозвана, истекла или уже использована: это норма.
        if (!cancelled) {
          if (caught instanceof ApiError && caught.status === 404) setGone(true)
          else setError(t.networkError)
        }
      })
    return () => {
      cancelled = true
    }
    // Текст ошибки берётся из словаря на момент ответа; перезапрашивать
    // приглашение при смене языка незачем.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (password !== repeat) {
      setError(t.inviteMismatch)
      return
    }
    setBusy(true)
    setError('')
    try {
      await api.acceptInvite(token, password)
      setDone(true)
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 404) setGone(true)
      else setError(caught instanceof ApiError && caught.message ? caught.message : t.networkError)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="login-screen">
      <BackgroundMap center={center} />

      <form className="login-card" onSubmit={submit}>
        <div className="brand">
          <span className="brand-mark">
            <TruckIcon />
          </span>
          <span className="brand-text">
            <h1>{t.appName}</h1>
            <p className="sub">{info ? t.inviteHello(info.name) : t.signInSub}</p>
          </span>
        </div>

        {gone && (
          <div className="login-error" role="alert">
            {t.inviteGone}
          </div>
        )}

        {done && (
          <>
            <p className="hint" role="status">
              {t.inviteDone}
            </p>
            <a className="primary" href="/">
              {t.inviteGo}
            </a>
          </>
        )}

        {!gone && !done && info && (
          <>
            {error && (
              <div className="login-error" role="alert">
                {error}
              </div>
            )}
            <label className="field">
              <span>{t.email}</span>
              <input value={info.email} readOnly autoComplete="username" />
            </label>
            <label className="field">
              <span>{t.inviteSetPassword}</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                minLength={MIN_PASSWORD}
                autoFocus
                required
              />
            </label>
            <label className="field">
              <span>{t.inviteRepeat}</span>
              <input
                type="password"
                value={repeat}
                onChange={(e) => setRepeat(e.target.value)}
                autoComplete="new-password"
                minLength={MIN_PASSWORD}
                required
              />
            </label>
            <p className="hint">{t.invitePasswordRule}</p>
            <button className="primary" type="submit" disabled={busy}>
              {busy ? t.working : t.inviteSetPassword}
            </button>
          </>
        )}

        {!gone && !done && !info && error && (
          <div className="login-error" role="alert">
            {error}
          </div>
        )}
      </form>

      <LangPicker lang={lang} onLang={onLang} />
    </div>
  )
}
